import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtNOK } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { RefreshCw, ShieldAlert, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const fmtVal = (kf) => {
  if (kf.value == null) return "—";
  if (kf.unit === "NOK") return fmtNOK(kf.value);
  if (kf.unit === "%") return `${kf.value} %`;
  return `${kf.value}`;
};

export default function ProffFinancials({ customerId, orgNumber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/customers/${customerId}/proff/financials`); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const remarks = (data?.liens || []).filter((l) => l.remark);
  const others = (data?.liens || []).filter((l) => !l.remark);
  return (
    <div className="bg-card border border-border rounded-xl p-6 mt-6" data-testid="proff-financials">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="font-display text-lg">Regnskap & kreditt (Proff)</h3>
        <Button size="sm" variant="secondary" data-testid="fetch-proff-financials-btn" onClick={load} disabled={loading || !orgNumber}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> {loading ? "Henter…" : (data ? "Oppdater" : "Hent fra Proff")}
        </Button>
      </div>
      {!data ? (
        <p className="text-sm text-muted-foreground" data-testid="proff-financials-empty">{loading ? "Henter…" : "Klikk «Hent fra Proff» for nøkkeltall, kredittvurdering og betalingsanmerkninger."}</p>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={`rounded-lg border p-3 ${data.has_payment_remark ? "border-red-500/40 bg-red-500/10" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid="payment-remarks-box">
              <div className="flex items-center gap-2">
                {data.has_payment_remark ? <ShieldAlert className="w-4 h-4 text-red-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
                <span className="label-caps text-muted-foreground">Betalingsanmerkninger</span>
              </div>
              <p className={`mt-1 text-sm font-medium ${data.has_payment_remark ? "text-red-300" : "text-emerald-300"}`}>
                {data.has_payment_remark ? `${remarks.length} tvungne heftelser/utlegg registrert` : "Ingen betalingsanmerkninger registrert"}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-secondary/40 p-3" data-testid="credit-rating-box">
              <span className="label-caps text-muted-foreground">Kredittvurdering</span>
              <p className="mt-1 text-sm font-medium">{data.credit_rating ? (typeof data.credit_rating === "object" ? JSON.stringify(data.credit_rating) : String(data.credit_rating)) : "Ikke tilgjengelig i Proff-datasettet"}</p>
            </div>
          </div>

          {data.key_figures?.length > 0 && (
            <div>
              <p className="label-caps text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Nøkkeltall {data.accounts_year ? `(${data.accounts_year})` : ""}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="proff-key-figures">
                {data.key_figures.map((kf) => (
                  <div key={kf.code} className="rounded-lg bg-secondary/40 border border-border p-3">
                    <p className="text-xs text-muted-foreground">{kf.label}</p>
                    <p className={`text-base font-medium ${kf.unit === "NOK" && kf.value < 0 ? "text-red-400" : ""}`}>{fmtVal(kf)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.history?.length > 0 && (
            <div>
              <p className="label-caps text-muted-foreground mb-2">Utvikling</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{["År", "Driftsinntekter", "Driftsresultat", "Årsresultat"].map((h) => <th key={h} className="px-3 py-1.5 label-caps">{h}</th>)}</tr></thead>
                <tbody>
                  {data.history.map((y) => (
                    <tr key={y.year} className="border-b border-border/50">
                      <td className="px-3 py-1.5 font-medium">{y.year}</td>
                      <td className="px-3 py-1.5">{y.revenue == null ? "—" : fmtNOK(y.revenue)}</td>
                      <td className={`px-3 py-1.5 ${y.operating_result < 0 ? "text-red-400" : ""}`}>{y.operating_result == null ? "—" : fmtNOK(y.operating_result)}</td>
                      <td className={`px-3 py-1.5 ${y.profit < 0 ? "text-red-400" : "text-emerald-400"}`}>{y.profit == null ? "—" : fmtNOK(y.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.liens?.length > 0 && (
            <div>
              <p className="label-caps text-muted-foreground mb-2">Pant og heftelser</p>
              <div className="space-y-1.5" data-testid="proff-liens">
                {[...remarks, ...others].map((l, i) => (
                  <div key={i} className={`flex items-center justify-between gap-2 rounded-lg border p-2 text-sm ${l.remark ? "border-red-500/30 bg-red-500/5" : "border-border bg-secondary/30"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${l.remark ? "bg-red-500/15 text-red-400 border border-red-500/30" : "bg-secondary text-muted-foreground border border-border"}`}>{l.group_label}</span>
                      <span>{l.type_label}</span>
                      {l.creditor && <span className="text-muted-foreground">· {l.creditor}</span>}
                    </div>
                    <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                      {l.amount != null && <span>{fmtNOK(l.amount)}</span>}{l.date && <span> · {l.date}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground/70">Kilde: Proff. Beløp i NOK. «Betalingsanmerkninger» utledes fra tvungne heftelser/utlegg tinglyst på selskapet.</p>
        </div>
      )}
    </div>
  );
}
